import BloodGlucoseClassification from "../../../../model/BloodGlucoseClassification";
import { BloodGlucoseObservation } from "../../../../model/BloodGlucoseObservation";
import cn from "../../../../util/cn";
import { Cell, CellProps } from "../../Cell";
import NewDataTooltipIndicator from "../../NewDataTooltipIndicator";

type FoodLogCellProps = {
  observation?: BloodGlucoseObservation;
  status: "default" | "new" | "disabled";
} & CellProps;

const readingBoxColorForClassification = (classification: BloodGlucoseClassification) => {
  switch (classification) {
    case "emergency-low":
      return "bg-measurement-background-emergency";
    case "critical-low":
      return "bg-measurement-background-warning";
    case "low":
      return "bg-measurement-background-acceptable";
    case "acceptable":
      return "bg-measurement-background-acceptable";
    case "elevated":
      return "bg-measurement-background-warning";
    case "critical-high":
      return "bg-measurement-background-emergency";
    default:
      return "bg-measurement-background-acceptable";
  }
};

const readingTextColorForClassification = (classification: BloodGlucoseClassification) => {
  switch (classification) {
    case "emergency-low":
      return "text-measurement-text-emergency";
    case "critical-low":
      return "text-measurement-text-warning";
    case "low":
      return "text-measurement-text-acceptable";
    case "acceptable":
      return "text-measurement-text-acceptable";
    case "elevated":
      return "text-measurement-text-warning";
    case "critical-high":
      return "text-measurement-text-emergency";
    default:
      return "text-measurement-text-acceptable";
  }
};

const FoodLogCell = ({ observation, status, className, ...otherProps }: FoodLogCellProps) => {
  const { reading, form } = observation ?? {};
  const { carbsInGrams, fatInGrams, proteinInGrams, mealContents = "" } = form ?? {};

  const isNew = status === "new";
  const isDisabled = status === "disabled";

  const readingBoxColor = !reading
    ? "bg-slate-100"
    : readingBoxColorForClassification(reading.classification);

  const readingTextColor = !reading
    ? "text-slate-600"
    : readingTextColorForClassification(reading.classification);

  const backgroundColor = status === "disabled" ? "bg-slate-100" : "bg-transparent";

  const carbsLine = carbsInGrams === undefined ? undefined : `Carbs: ${carbsInGrams}g`;
  const fatLine = fatInGrams === undefined ? undefined : `Fat: ${fatInGrams}g`;
  const proteinLine = proteinInGrams === undefined ? undefined : `Protein: ${proteinInGrams}g`;
  const macrosLines = [carbsLine, fatLine, proteinLine].filter(Boolean);

  const hasData = reading || mealContents.length > 0 || macrosLines.length > 0;

  return (
    <Cell
      className={cn(
        "relative flex cursor-pointer p-2",
        backgroundColor,
        isDisabled ? "cursor-default" : "cursor-pointer",
        className
      )}
      outline
      {...otherProps}
    >
      {isNew && (
        <div className="absolute right-2 top-2">
          <NewDataTooltipIndicator />
        </div>
      )}
      {isDisabled ? null : !hasData ? (
        <div className="flex h-full w-full items-center justify-center font-semibold text-primary/60">
          -
        </div>
      ) : (
        <div className="flex grow flex-col">
          <p className={cn("text-pretty text-sm", isNew ? "me-4" : "me-0")}>
            {mealContents.length > 0 ? (
              <span className="font-semibold text-primary/90">{mealContents}</span>
            ) : (
              <span className="italic text-primary/60">No meal contents provided</span>
            )}
          </p>
          <div className="mt-2 flex grow items-end justify-between">
            <div className="pe-2">
              {macrosLines.map((line) => (
                <p key={line} className="truncate text-sm text-primary/60">
                  {line}
                </p>
              ))}
            </div>
            {reading && (
              <span
                className={cn(
                  "inline-block rounded-md px-[8px] py-[1px] font-semibold text-primary/90",
                  readingBoxColor,
                  readingTextColor
                )}
              >
                {reading?.bloodGlucoseInMilligramsPerDeciliter.toString() ?? ""}
              </span>
            )}
          </div>
        </div>
      )}
    </Cell>
  );
};

export default FoodLogCell;
